import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Skeleton,
  useTheme
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon
} from '@mui/icons-material';

/**
 * Individual Metric Card Component
 */
const MetricCard = ({ 
  title, 
  value, 
  previousValue, 
  icon, 
  color = 'primary',
  loading = false 
}) => {
  const theme = useTheme();
  
  // Calculate change percentage
  const changePercent = previousValue ? ((value - previousValue) / previousValue * 100) : 0;
  const isPositive = changePercent > 0;
  const isFlat = Math.abs(changePercent) < 0.1;
  
  const getTrendIcon = () => {
    if (isFlat) return <TrendingFlatIcon fontSize="small" />;
    return isPositive ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />;
  };
  
  const getTrendColor = () => {
    if (isFlat) return 'text.secondary';
    return isPositive ? 'success.main' : 'error.main';
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" height={40} sx={{ my: 1 }} />
              <Skeleton variant="text" width="80%" />
            </Box>
            <Skeleton variant="circular" width={56} height={56} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              color="text.secondary" 
              gutterBottom 
              variant="body2"
              sx={{ fontWeight: 500 }}
            >
              {title}
            </Typography>
            
            <Typography 
              variant="h4" 
              component="div" 
              sx={{ 
                mb: 1,
                fontWeight: 600,
                color: 'text.primary'
              }}
            >
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
            
            {previousValue !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: getTrendColor(),
                  mr: 1
                }}>
                  {getTrendIcon()}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      ml: 0.5,
                      fontWeight: 600
                    }}
                  >
                    {isPositive ? '+' : ''}{Math.abs(changePercent).toFixed(1)}%
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: '0.75rem' }}
                >
                  vs last period
                </Typography>
              </Box>
            )}
          </Box>
          
          <Avatar
            sx={{
              bgcolor: `${color}.light`,
              color: `${color}.main`,
              width: 56,
              height: 56,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * Analytics Overview Cards Component
 * 
 * Displays a grid of metric cards with key performance indicators
 * 
 * @param {Object} props - Component props
 * @param {Array} props.metrics - Array of metric objects
 * @param {boolean} props.loading - Loading state
 * @param {number} props.cols - Number of columns (responsive breakpoints)
 */
const AnalyticsOverviewCards = ({ 
  metrics = [], 
  loading = false,
  cols = {
    xs: 12,
    sm: 6,
    md: 4,
    lg: 2
  }
}) => {
  if (!metrics || metrics.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {metrics.map((metric, index) => (
        <Grid 
          item 
          xs={cols.xs} 
          sm={cols.sm} 
          md={cols.md} 
          lg={cols.lg} 
          key={metric.title || index}
        >
          <MetricCard
            title={metric.title}
            value={metric.value}
            previousValue={metric.previousValue}
            icon={metric.icon}
            color={metric.color}
            loading={loading}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default AnalyticsOverviewCards;